import { getDocs, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { uploadFile, getActiveDiscounts } from '../firebase/services';
import { useCart } from '../Context/CartContext';
import { useLanguage, getLanguageText } from '../Context/LanguageContext';
import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Checkout.css';
import SEO from '../Components/SEO';
import { notifyNewOrderViaTelegram } from '../Utils/telegram';

export default function Checkout() {
  const { cartItems, getCartTotal, removeFromCart, getCartItemPricing } = useCart();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [buyNowItem, setBuyNowItem] = useState(null);
  const [itemsToDisplay, setItemsToDisplay] = useState([]);
  const [isDirectCheckout, setIsDirectCheckout] = useState(false);
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedShippingRate, setSelectedShippingRate] = useState(0);
  const [discounts, setDiscounts] = useState([]);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canonicalCheckout = typeof window !== 'undefined'
    ? `${window.location.origin}/checkout`
    : '';

  // Form State
  const [formData, setFormData] = useState({
    email: '',
    newsOffers: true,
    country: 'Egypt',
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    governorate: '',
    postalCode: '',
    phone: '',
    saveInfo: false,
    textNews: false,
    shippingMethod: 'standard',
    paymentMethod: 'cod',
    billingAddress: 'same'
  });

  const [paymentEvidence, setPaymentEvidence] = useState(null);
  const [evidencePreview, setEvidencePreview] = useState('');

  useEffect(() => {
    // Check if this is a direct checkout from ProductDetails
    if (location.state?.isDirectCheckout && location.state?.directCheckoutItem) {
      const directItem = location.state.directCheckoutItem;
      setBuyNowItem(directItem);
      setItemsToDisplay([directItem]);
      setIsDirectCheckout(true);
    } else {
      // Check sessionStorage for backward compatibility
      const buyNowData = sessionStorage.getItem('buyNowItem');
      if (buyNowData) {
        try {
          const item = JSON.parse(buyNowData);
          setBuyNowItem(item);
          setItemsToDisplay([item]);
          setIsDirectCheckout(true);
          sessionStorage.removeItem('buyNowItem');
        } catch (e) {
          console.error('Error parsing buyNowItem', e);
          setItemsToDisplay(cartItems);
        }
      } else {
        setItemsToDisplay(cartItems);
      }
    }
  }, [cartItems, location.state]);

  // Checkout should not be indexed
  const checkoutSeo = (
    <SEO
      title="Checkout"
      description="Secure checkout - VetCare Store"
      noIndex={true}
      canonical={canonicalCheckout}
      type="website"
    />
  );

  useEffect(() => {
    fetchShippingRates();
    fetchDiscounts();
  }, []);

  useEffect(() => {
    applyAutoDiscounts();
  }, [discounts]);

  const applyAutoDiscounts = () => {
    console.log('Applying auto discounts...', { discounts, subtotal });
    
    // Apply automatic discounts (NOT coupon type)
    const autoDiscounts = discounts.filter(discount => 
      discount.isActive && 
      discount.targetType !== 'coupon' && // Only automatic discounts
      (!discount.expiryDate || new Date(discount.expiryDate) > new Date()) &&
      (!discount.minAmount || subtotal >= discount.minAmount)
    );

    console.log('Found auto discounts:', autoDiscounts);

    if (autoDiscounts.length > 0) {
      // Apply the first applicable auto discount
      setSelectedDiscount(autoDiscounts[0]);
      toast.success(`Auto discount applied: ${autoDiscounts[0].couponCode || autoDiscounts[0].name}`);
      console.log('Applied discount:', autoDiscounts[0]);
    }
  };

  const fetchDiscounts = async () => {
    try {
      const activeDiscounts = await getActiveDiscounts();
      setDiscounts(activeDiscounts);
    } catch (error) {
      console.error('Error fetching discounts:', error);
    }
  };

  const fetchShippingRates = async () => {
    try {
      const ratesQuery = collection(db, 'shippingRates');
      const querySnapshot = await getDocs(ratesQuery);
      const ratesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setShippingRates(ratesData);
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'governorate') {
      const selectedRate = shippingRates.find(rate => 
        rate.governorate.toLowerCase() === value.toLowerCase()
      );
      setSelectedShippingRate(selectedRate ? selectedRate.price : 0);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setPaymentEvidence(e.target.files[0]);
      setEvidencePreview(e.target.files[0].name);
    }
  };

  const handleApplyDiscount = () => {
    console.log('Applying discount code:', discountCode);
    console.log('Available discounts:', discounts);
    
    // Only look for coupon type discounts
    const discount = discounts.find(d => 
      d.targetType === 'coupon' && // Only coupon codes
      d.couponCode === discountCode
    );
    console.log('Found discount:', discount);
    
    if (discount) {
      // Check if discount meets minimum amount requirement
      if (discount.minAmount && subtotal < discount.minAmount) {
        toast.error(`Minimum order amount is ${discount.minAmount} EGP`);
        return;
      }
      
      setSelectedDiscount(discount);
      toast.success('Coupon applied!');
    } else {
      toast.error('Invalid coupon code');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.address || !formData.city || !formData.governorate || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if ((formData.paymentMethod === 'vodafone_cash' || formData.paymentMethod === 'instapay') && !paymentEvidence) {
      toast.error('Please upload payment evidence');
      return;
    }

    setIsSubmitting(true);

    try {
      let evidenceUrl = '';
      
      // Upload evidence if exists
      if (paymentEvidence) {
        evidenceUrl = await uploadFile(paymentEvidence);
      }

      const orderItems = displayItems.map(item => ({
        id: item.id,
        name: getLanguageText(item.name, language),
        price: (getCartItemPricing ? getCartItemPricing(item).finalUnitPrice : (item.selectedPrice || item.price)),
        quantity: item.quantity || 1,
        category: item.category || '',
        selectedTaste: item.selectedTaste ? getLanguageText(item.selectedTaste, language) : '',
        size: item.size || '',
        sizeUnit: item.sizeUnit || '',
        imageUrl: item.imageUrl || ''
      }));

      const orderData = {
        customerName: `${formData.firstName} ${formData.lastName}`,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        customerAddress: `${formData.address}, ${formData.apartment ? formData.apartment + ', ' : ''}${formData.city}, ${formData.governorate}`,
        items: orderItems,
        subtotal: subtotal,
        shippingFee: selectedShippingRate,
        discountAmount: discountAmount,
        totalAmount: total,
        shippingMethod: formData.shippingMethod,
        paymentMethod: formData.paymentMethod,
        paymentEvidenceUrl: evidenceUrl,
        status: 'pending',
        createdAt: serverTimestamp(),
        notes: formData.apartment ? `Apartment: ${formData.apartment}` : ''
      };

      const docRef = await addDoc(collection(db, 'orders'), orderData);

      // Insecure client-side notification (Telegram bot token is exposed in the bundle)
      // Fire-and-forget so checkout is not blocked.
      Promise.resolve()
        .then(() => notifyNewOrderViaTelegram({
          ...orderData,
          id: docRef?.id,
          baseUrl: typeof window !== 'undefined' ? window.location.origin : '',
        }))
        .then((res) => {
          if (res && res.ok !== true) {
            // Useful when DevTools is set to Offline or chat_id/token are missing.
            console.warn('[telegram] notification skipped', res);
          }
        })
        .catch((err) => {
          console.warn('[telegram] notification failed', err);
        });
      
      if (!buyNowItem) {
        cartItems.forEach(item => removeFromCart(item.id));
      }

      toast.success('Order placed successfully!');
      
      // Navigate to order confirmation page with all data
      const customerData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        apartment: formData.apartment,
        city: formData.city,
        governorate: formData.governorate,
        postalCode: formData.postalCode
      };

      navigate('/order-confirmation', { 
        state: { 
          customerData, 
          orderData: displayItems,
          totalAmount: total,
          discountAmount: discountAmount,
          subtotal: subtotal,
          shippingCost: selectedShippingRate
        } 
      });

    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculations
  const displayItems = itemsToDisplay.length > 0 ? itemsToDisplay : cartItems;
  
  let subtotal = 0;
  if (buyNowItem) {
    const buyNowPricing = getCartItemPricing
      ? getCartItemPricing(buyNowItem)
      : { finalUnitPrice: (buyNowItem.selectedPrice || buyNowItem.price || 0) };
    subtotal = (buyNowPricing.finalUnitPrice || 0) * (buyNowItem.quantity || 1);
  } else {
    subtotal = getCartTotal();
  }

  let discountAmount = 0;
  if (selectedDiscount) {
    console.log('Calculating discount for:', selectedDiscount);
    console.log('Subtotal before discount:', subtotal);
    
    const applicableSubtotal = subtotal;
    if (selectedDiscount.minAmount && applicableSubtotal < selectedDiscount.minAmount) {
      console.log('Discount not applicable - minimum amount not met');
      // Discount not applicable
    } else {
      // Convert value to number to ensure correct calculation
      const discountValue = parseFloat(selectedDiscount.value) || 0;
      console.log('Discount type:', selectedDiscount.type, 'Discount value:', discountValue);
      
      if (selectedDiscount.type === 'percentage') {
        discountAmount = (applicableSubtotal * discountValue) / 100;
        console.log('Percentage discount calculation:', `${applicableSubtotal} * ${discountValue} / 100 = ${discountAmount}`);
      } else if (selectedDiscount.type === 'fixed') {
        discountAmount = Math.min(discountValue, applicableSubtotal);
        console.log('Fixed discount calculation:', `min(${discountValue}, ${applicableSubtotal}) = ${discountAmount}`);
      }
      
      console.log('Final discount amount:', discountAmount);
    }
  }

  const total = subtotal + selectedShippingRate - discountAmount;

  if (itemsToDisplay.length === 0 && !buyNowItem) {
    return (
      <>
        {checkoutSeo}
        <div style={{ padding: '120px 20px 40px', textAlign: 'center', minHeight: '60vh' }}>
          <h2 style={{ fontSize: '2rem', color: '#333' }}>Checkout</h2>
          <p style={{ fontSize: '1rem', color: '#666', marginTop: '20px' }}>Your cart is empty.</p>
          <Link to="/shop" className="btn-primary" style={{ marginTop: '20px', display: 'inline-block' }}>Continue Shopping</Link>
        </div>
      </>
    );
  }

  return (
    <>
      {checkoutSeo}
      <div className="checkout-page">
        <div className="checkout-container">
        
        {/* Left Column - Form */}
        <div className="form-section">
          <form onSubmit={handleSubmit}>
            
            {/* Contact */}
            <div className="section-title">
              <span>Contact</span>
              {!formData.email && <Link to="/login">Log in</Link>}
            </div>
            <div className="form-group">
              <input 
                type="text" 
                name="email"
                className="form-input" 
                placeholder="Email or mobile phone number"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  name="newsOffers"
                  className="checkbox-input"
                  checked={formData.newsOffers}
                  onChange={handleChange}
                />
                Email me with news and offers
              </label>
            </div>

            {/* Delivery */}
            <div className="section-title" style={{ marginTop: '30px' }}>
              <span>Delivery</span>
            </div>
            <div className="form-group">
              <select 
                name="country" 
                className="form-select"
                value={formData.country}
                onChange={handleChange}
                disabled
              >
                <option value="Egypt">Egypt</option>
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <input 
                  type="text" 
                  name="firstName"
                  className="form-input" 
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <input 
                  type="text" 
                  name="lastName"
                  className="form-input" 
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <input 
                type="text" 
                name="address"
                className="form-input" 
                placeholder="Address"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <input 
                type="text" 
                name="apartment"
                className="form-input" 
                placeholder="Apartment, suite, etc. (optional)"
                value={formData.apartment}
                onChange={handleChange}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <input 
                  type="text" 
                  name="city"
                  className="form-input" 
                  placeholder="City"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <select 
                  name="governorate" 
                  className="form-select"
                  value={formData.governorate}
                  onChange={handleChange}
                  required
                >
                  <option value="">Governorate</option>
                  {shippingRates.map(rate => (
                    <option key={rate.id} value={rate.governorate}>{rate.governorate}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <input 
                  type="text" 
                  name="postalCode"
                  className="form-input" 
                  placeholder="Postal code (optional)"
                  value={formData.postalCode}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-group">
              <input 
                type="tel" 
                name="phone"
                className="form-input" 
                placeholder="Phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  name="saveInfo"
                  className="checkbox-input"
                  checked={formData.saveInfo}
                  onChange={handleChange}
                />
                Save this information for next time
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  name="textNews"
                  className="checkbox-input"
                  checked={formData.textNews}
                  onChange={handleChange}
                />
                Text me with news and offers
              </label>
            </div>

            {/* Shipping Method */}
            <div className="section-title" style={{ marginTop: '30px' }}>
              <span>Shipping method</span>
            </div>
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '5px', 
              display: 'flex', 
              justifyContent: 'space-between',
              border: '1px solid #d9d9d9'
            }}>
              <span>Standard Shipping</span>
              <span style={{ fontWeight: 'bold' }}>EGP {selectedShippingRate.toFixed(2)}</span>
            </div>

            {/* Payment */}
            <div className="section-title" style={{ marginTop: '30px' }}>
              <span>Payment</span>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px' }}>
              All transactions are secure and encrypted.
            </p>
            
            <div className="payment-methods">
              {/* COD */}
              <div className="payment-option">
                <div 
                  className={`payment-header ${formData.paymentMethod === 'cod' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, paymentMethod: 'cod'})}
                >
                  <input 
                    type="radio" 
                    name="paymentMethod" 
                    value="cod"
                    checked={formData.paymentMethod === 'cod'}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span>Cash on Delivery (COD)</span>
                </div>
                {formData.paymentMethod === 'cod' && (
                  <div className="payment-content">
                    <p>Pay with cash upon delivery.</p>
                  </div>
                )}
              </div>

              {/* Vodafone Cash */}
              <div className="payment-option">
                <div 
                  className={`payment-header ${formData.paymentMethod === 'vodafone_cash' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, paymentMethod: 'vodafone_cash'})}
                >
                  <input 
                    type="radio" 
                    name="paymentMethod" 
                    value="vodafone_cash"
                    checked={formData.paymentMethod === 'vodafone_cash'}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span>Vodafone Cash</span>
                </div>
                {formData.paymentMethod === 'vodafone_cash' && (
                  <div className="payment-content">
                    <p className="payment-instruction">
                      Transfer the price to <strong>01001476621</strong> Vodafone cash
                    </p>
                    <div className="upload-btn-wrapper">
                      <button type="button" className="btn-upload">Upload Payment Evidence</button>
                      <input type="file" name="evidence" onChange={handleFileChange} className="upload-input" accept="image/*" />
                    </div>
                    {evidencePreview && <span className="file-name">{evidencePreview}</span>}
                  </div>
                )}
              </div>

              {/* InstaPay */}
              <div className="payment-option">
                <div 
                  className={`payment-header ${formData.paymentMethod === 'instapay' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, paymentMethod: 'instapay'})}
                >
                  <input 
                    type="radio" 
                    name="paymentMethod" 
                    value="instapay"
                    checked={formData.paymentMethod === 'instapay'}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span>InstaPay</span>
                </div>
                {formData.paymentMethod === 'instapay' && (
                  <div className="payment-content">
                    <p className="payment-instruction">
                      Click on this link to transfer to InstaPay:<br/>
                      <a href="https://ipn.eg/S/mohebmikhael/instapay/6Ely2O" target="_blank" rel="noreferrer" className="payment-link">
                        https://ipn.eg/S/mohebmikhael/instapay/6Ely2O
                      </a>
                    </p>
                    <div className="upload-btn-wrapper">
                      <button type="button" className="btn-upload">Upload Payment Evidence</button>
                      <input type="file" name="evidence" onChange={handleFileChange} className="upload-input" accept="image/*" />
                    </div>
                    {evidencePreview && <span className="file-name">{evidencePreview}</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Billing Address */}
            <div className="section-title" style={{ marginTop: '30px' }}>
              <span>Billing address</span>
            </div>
            <div className="payment-methods">
              <div className="payment-option">
                <div 
                  className={`payment-header ${formData.billingAddress === 'same' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, billingAddress: 'same'})}
                >
                  <input 
                    type="radio" 
                    name="billingAddress" 
                    value="same"
                    checked={formData.billingAddress === 'same'}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span>Same as shipping address</span>
                </div>
              </div>
              <div className="payment-option">
                <div 
                  className={`payment-header ${formData.billingAddress === 'different' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, billingAddress: 'different'})}
                >
                  <input 
                    type="radio" 
                    name="billingAddress" 
                    value="different"
                    checked={formData.billingAddress === 'different'}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span>Use a different billing address</span>
                </div>
              </div>
            </div>

            <button type="submit" className="btn-complete" disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : 'Complete order'}
            </button>

            <div className="footer-links">
              <span className="footer-link">Refund policy</span>
              <span className="footer-link">Shipping</span>
              <span className="footer-link">Privacy policy</span>
              <span className="footer-link">Terms of service</span>
              <span className="footer-link">Contact</span>
            </div>

          </form>
        </div>

        {/* Right Column - Summary */}
        <div className="summary-section">
          {itemsToDisplay.map(item => (
            <div key={item.id} className="summary-item">
              <div className="item-image-wrapper">
                <img 
                  src={(item.imageUrls && item.imageUrls[0]) || (item.images && item.images[0]) || item.imageUrl || 'https://placehold.co/80x80'} 
                  alt={item.name} 
                  className="item-image"
                />
                <span className="item-qty">{item.quantity}</span>
              </div>
              <div className="item-details">
                <div className="item-name">{getLanguageText(item.name, language)}</div>
                {item.size && <div className="item-variant">{item.size}{item.sizeUnit}</div>}
              </div>
              <div className="item-price">
                {(() => {
                  const unit = getCartItemPricing
                    ? (getCartItemPricing(item).finalUnitPrice || 0)
                    : (item.selectedPrice || item.price || 0);
                  return `EGP ${(unit * (item.quantity || 1)).toFixed(2)}`;
                })()}
              </div>
            </div>
          ))}

          {/* Show coupon section only if no auto discount is applied */}
          {(!selectedDiscount || selectedDiscount.targetType === 'coupon') && (
            <div className="discount-form">
              <input 
                type="text" 
                className="form-input" 
                placeholder="Discount code or gift card"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
              />
              <button 
                type="button" 
                className="btn-apply" 
                onClick={handleApplyDiscount}
                disabled={!discountCode}
              >
                Apply
              </button>
            </div>
          )}

          {/* Show auto discount info */}
          {selectedDiscount && selectedDiscount.targetType !== 'coupon' && (
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#e8f5e8', 
              borderRadius: '8px', 
              marginBottom: '15px',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: 'bold', color: '#27ae60', marginBottom: '5px' }}>
                Auto Discount Applied
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>
                {selectedDiscount.name || 'Automatic discount'} - {selectedDiscount.value}% off
              </div>
            </div>
          )}

          <div className="summary-row">
            <span>Subtotal</span>
            <span>EGP {subtotal.toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Shipping <ion-icon name="help-circle-outline"></ion-icon></span>
            <span>{selectedShippingRate > 0 ? `EGP ${selectedShippingRate.toFixed(2)}` : 'Calculated at next step'}</span>
          </div>
          {discountAmount > 0 && (
            <div className="summary-row" style={{ color: '#0d918b' }}>
              <span>Discount</span>
              <span>- EGP {discountAmount.toFixed(2)}</span>
            </div>
          )}

          <div className="summary-total">
            <span>Total</span>
            <div>
              <span className="total-currency">EGP</span>
              <span>{total.toFixed(2)}</span>
            </div>
          </div>
        </div>

      </div>
      </div>
    </>
  );
}
